'use client';

import {
  type ComponentProps,
  type CSSProperties,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslations } from 'next-intl';
import { AdornedInput, Modal, Spinner } from '@zillow/constellation-10';
import { interactionResponse } from '@zillow/yield-callback';
import { MapPin } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import type { AreaResponse, AutocompleteResult } from '@/src/lib/api';
import {
  groupByHeading,
  LOCATION_MIN_QUERY_LENGTH,
  type LocationAnalyticsSurface,
  type OptionShape,
  useLocationSuggestions,
} from './useLocationSuggestions';
import * as styles from './locationSearchModal.styles';
import { useModalDismissProps } from '@/src/lib/hooks/ui/useModalDismissProps';

// C11n 10.20 types `focusManagerProps` as the whole `FloatingFocusManagerProps`,
// including `children` and `context` — but `Modal.Content` supplies both itself
// and spreads this object over them. Cast so a partial override doesn't have to
// invent a context it has no access to.
type ModalFocusManagerProps = ComponentProps<typeof Modal.Content>['focusManagerProps'];

export type LocationSearchModalProps = {
  onClose: () => void;
  nearMeArea?: AreaResponse | null;
  onSelect: (result: AutocompleteResult) => void;
  onEnterSearch: (result: AutocompleteResult) => void;
  onUserInput: () => void;
  /**
   * Which bar opened this, for clickstream attribution — the dialog renders
   * identically everywhere, so it can't be inferred from a size variant.
   */
  analyticsSurface: LocationAnalyticsSurface;
};

/**
 * Full-screen location search for viewports below `lg` (HP-8998).
 *
 * Deliberately NOT a Constellation `Combobox`. A Combobox is a collapsible
 * popup anchored to an input — exactly right for the desktop dropdown, and
 * exactly wrong here, where the list is the entire content of the dialog and
 * never collapses. Using one meant pinning it permanently open, portaling the
 * panel back into the dialog it already lived inside, disabling its Escape
 * handling so the dialog could have Escape, overriding away its card, its
 * trigger-derived width and its hardcoded 360px cap, and measuring the visual
 * viewport to compensate for a floating panel not participating in layout.
 *
 * A field over a `role="listbox"` in normal flow needs none of that: the list
 * fills the remaining height and scrolls.
 *
 * The trade is real, though: Combobox did provide arrow/Home/End navigation for
 * free (floating-ui's `useListNavigation`, which is bound to the floating
 * context and so can't be used without the popup), and that is re-implemented
 * below against `aria-activedescendant`. The Enter rule is NOT re-implemented —
 * it always needed its own layer on top of Constellation, and both
 * presentations now resolve it through `useLocationSuggestions.resolveEnter`.
 *
 * Mounted only while open, so dismissing discards the draft query without ever
 * touching the selected area.
 */
export default function LocationSearchModal({
  onClose,
  nearMeArea,
  onSelect,
  onEnterSearch,
  onUserInput,
  analyticsSurface,
}: LocationSearchModalProps) {
  const modalDismissProps = useModalDismissProps();
  const tSearch = useTranslations('search');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const { idPrefix, inputValue, loading, options, handleInputValueChange, selectOption, searchOption, resolveEnter } =
    useLocationSuggestions({
      // Starts empty rather than seeded with the selected area: a formatted area
      // label is longer than the autocomplete's minimum query, so seeding one
      // puts the list on the results branch with nothing fetched — an empty
      // dialog, which on the SRP would be every single tap. Empty surfaces Near
      // Me and Recently Viewed straight away.
      area: null,
      nearMeArea,
      onSelect,
      onEnterSearch,
      onUserInput,
      analyticsSurface,
    });

  const listboxId = `${idPrefix}-listbox`;
  const optionId = useCallback((index: number) => `${idPrefix}-option-${index}`, [idPrefix]);

  // Grouped here rather than in the hook: only this presentation renders
  // headings, and grouping reorders the list, so the flat index the highlight
  // and Enter use has to follow what's actually on screen.
  const { indexedGroups, displayOrder } = useMemo(() => {
    const order: Array<OptionShape> = [];
    const groups = groupByHeading(options).map((group) => ({
      heading: group.heading,
      items: group.items.map((item) => {
        order.push(item);
        return { item, index: order.length - 1 };
      }),
    }));
    return { indexedGroups: groups, displayOrder: order };
  }, [options]);

  // Keep the highlighted row on screen — arrow keys can walk past the fold.
  // Also re-runs when the list changes: a row keeps its index across a result
  // swap but not its offset, so the highlight can end up off screen.
  useEffect(() => {
    if (activeIndex < 0) return;
    const row = listRef.current?.querySelector(`[data-option-index="${activeIndex}"]`);
    // jsdom doesn't implement scrollIntoView, and it's a nice-to-have anyway.
    if (row instanceof Element && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, displayOrder]);

  // Which suggestion the current press started on. Rows are keyed by position,
  // so a row's node survives a result swap and only its contents change — without
  // this, a fetch landing between press and release commits whatever took that
  // row's place rather than what the user actually tapped.
  const pressedValueRef = useRef<string | null>(null);

  const commit = useCallback(
    async (option: OptionShape) => {
      selectOption(option);
      // Yield so the pressed row paints before the dialog unmounts. Without it
      // the selection is committed and the list torn down in the same task, so
      // the tap never visibly registers. Same primitive Constellation's Modal
      // awaits before it closes on a dismissal or a close-button click.
      await interactionResponse();
      onClose();
    },
    [onClose, selectOption],
  );

  const moveActive = useCallback(
    (delta: number) => {
      if (displayOrder.length === 0) return;
      setActiveIndex((current) => {
        const next = current + delta;
        if (next < 0) return displayOrder.length - 1;
        if (next >= displayOrder.length) return 0;
        return next;
      });
    },
    [displayOrder.length],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.nativeEvent.isComposing) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveActive(1);
          return;
        case 'ArrowUp':
          event.preventDefault();
          moveActive(-1);
          return;
        case 'Home':
          if (displayOrder.length === 0) return;
          event.preventDefault();
          setActiveIndex(0);
          return;
        case 'End':
          if (displayOrder.length === 0) return;
          event.preventDefault();
          setActiveIndex(displayOrder.length - 1);
          return;
        case 'Enter': {
          // Holding Enter auto-repeats keydowns; only the first press searches.
          if (event.repeat) {
            event.preventDefault();
            return;
          }
          // Same HP-8246 rule the inline field uses — resolved by the hook so
          // the two presentations can't drift apart.
          const resolution = resolveEnter(activeIndex >= 0 ? displayOrder[activeIndex] : null, displayOrder[0]);
          if (resolution.kind === 'fallthrough') return;
          event.preventDefault();
          if (resolution.kind === 'blocked') return;
          searchOption(resolution.option);
          onClose();
          return;
        }
        default:
          return;
      }
    },
    [activeIndex, displayOrder, moveActive, onClose, resolveEnter, searchOption],
  );

  const hasQuery = inputValue.trim().length >= LOCATION_MIN_QUERY_LENGTH;
  // Only while there is nothing to show: re-typing over existing results keeps
  // them on screen rather than replacing the list with a spinner on every
  // keystroke.
  const showLoading = loading && displayOrder.length === 0;
  // Gated on `loading`, or the debounce + fetch window reports "no matches" for
  // a query that is still in flight.
  const showEmptyState = !loading && displayOrder.length === 0 && hasQuery;

  return (
    <Modal.Root
      useDismissProps={modalDismissProps}
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size={{ base: 'fullScreen', lg: 'md' }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content
            data-testid="listing-search-location-modal"
            // No `Modal.Header`, so nothing supplies `aria-labelledby` — name
            // the dialog directly or it reaches screen readers unlabelled.
            aria-label={tSearch('searchForLocation')}
            // Otherwise the trap parks focus on the dialog container a tick
            // after mount, so on mobile the keyboard never opens and the first
            // keystrokes go nowhere.
            focusManagerProps={{ initialFocus: inputRef } as ModalFocusManagerProps}
            className={styles.dialogContent}
          >
            {/* Reserves room for Modal.CloseButton, which c11n positions
                absolutely against Modal.Content rather than in flow. */}
            <div className={styles.fieldRow}>
              <AdornedInput
                fluid
                startAdornment={
                  <AdornedInput.Adornment>
                    <LucideIcon icon={MapPin} size="md" />
                  </AdornedInput.Adornment>
                }
                input={
                  <AdornedInput.Input
                    ref={inputRef}
                    role="combobox"
                    aria-expanded
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
                    aria-label={tSearch('searchByLocation')}
                    autoComplete="off"
                    enterKeyHint="search"
                    // `id` rather than `name` (HP-9041): nothing read the name — this input has
                    // no form — and it was the stable key Chrome filed history under. The id
                    // still satisfies the DevTools id-or-name requirement.
                    id={`${idPrefix}-input`}
                    placeholder={tSearch('findApartmentsIn')}
                    type="search"
                    value={inputValue}
                    onChange={(event) => {
                      handleInputValueChange(event.target.value);
                      setActiveIndex(-1);
                    }}
                    onKeyDown={handleKeyDown}
                    data-testid="AutocompleteV2InputFullscreen"
                  />
                }
              />
            </div>

            {/* The scroll container is the wrapper, not the listbox: ARIA lets a
                listbox own only `option` and `group`, so the loading and empty
                rows have to be siblings of it rather than children. */}
            <div className={styles.suggestionList}>
              {/* Groups are positioned by transform from the counts below, not by flow, so a
                  group gaining or losing a row no longer moves the ones after it in layout
                  terms — see `locationSearchModal.styles.ts` for why that is what CLS cares
                  about, and for the fixed box sizes it costs. */}
              <div
                ref={listRef}
                id={listboxId}
                role="listbox"
                aria-label={tSearch('searchSuggestions')}
                className={styles.listbox}
                style={
                  {
                    '--list-groups': indexedGroups.length,
                    '--list-rows': displayOrder.length,
                  } as CSSProperties
                }
              >
                {/* Groups key by heading, rows by position — deliberate opposites.

                    Groups (HP-9107): an index key re-purposes a surviving node into a
                    different heading. "denver" -> "denver c" took Cities 10/Properties 5 to
                    Cities 2/Neighborhoods 8/Properties 5, so group node 1 stopped being
                    Properties and became Neighborhoods — the same node, eight rows up, billed
                    at 0.4364 in the field. By heading, Properties stays put to within one
                    heading's height and Neighborhoods mounts fresh, which CLS never bills.
                    `groupByHeading` buckets on exactly this string, so it is unique.

                    Rows: positional, not `item.value` — re-ranking keeps surviving rows
                    mounted and moves them hundreds of px. Position holds each node still and
                    swaps its text; rows carry no state, and `activeIndex` is already
                    positional. */}
                {indexedGroups.map((group, groupIndex) => (
                  <div
                    key={group.heading}
                    role="group"
                    aria-label={group.heading}
                    className={styles.suggestionGroup}
                    style={
                      {
                        '--groups-before': groupIndex,
                        // The first row's global index is exactly the row count before it.
                        '--rows-before': group.items[0]?.index ?? 0,
                      } as CSSProperties
                    }
                  >
                    <div className={styles.groupHeading} aria-hidden="true">
                      {group.heading}
                    </div>
                    {group.items.map(({ item, index }) => (
                      // eslint-disable-next-line jsx-a11y/interactive-supports-focus -- aria-activedescendant pattern: focus stays on the field, so options must NOT be tabbable
                      <div
                        key={index}
                        id={optionId(index)}
                        role="option"
                        aria-selected={index === activeIndex}
                        data-option-index={index}
                        data-highlighted={index === activeIndex ? '' : undefined}
                        data-testid="location-suggestion"
                        className={styles.suggestionRow}
                        // Click, not pointerdown: committing on press makes the
                        // list impossible to drag-scroll, because the first touch
                        // or mouse-down on a row selects it. A click only fires
                        // when press and release land on the same row, so a drag
                        // scrolls instead. Focus stays on the field regardless —
                        // these rows carry no tabindex, so they can't take it.
                        onPointerDown={() => {
                          pressedValueRef.current = item.value;
                        }}
                        onClick={() => {
                          // Still the suggestion that was pressed? If a fetch landed
                          // mid-press this row now holds something else, and the tap
                          // is dropped rather than committing the wrong area.
                          if (pressedValueRef.current !== item.value) return;
                          void commit(item);
                        }}
                        onMouseEnter={() => setActiveIndex(index)}
                        // Without this the last hovered row stays highlighted for
                        // good — and `aria-activedescendant` keeps pointing at it,
                        // telling assistive tech the user is on a row they left.
                        onMouseLeave={() => setActiveIndex(-1)}
                      >
                        <span className={styles.rowIcon}>
                          <LucideIcon icon={MapPin} size="sm" />
                        </span>
                        <span className={styles.rowLabel}>{item.label}</span>
                        {item.meta ? <span className={styles.rowMeta}>{item.meta}</span> : null}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {showLoading ? (
                <div className={styles.statusRow}>
                  <Spinner title={tSearch('searching')} />
                </div>
              ) : null}
              {showEmptyState ? (
                <div className={styles.emptyState} role="status">
                  {tSearch('noAreaResults')}
                </div>
              ) : null}
            </div>

            <Modal.CloseButton data-testid="listing-search-location-modal-close" />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}
